<!--
============================================================
TVD Side Drawer - Template Structure Overview
============================================================

This template uses the Neptune/Tailwind design system
(refreshed UI). The legacy UI version has been removed.


Layout Type
------------------------------------------------------------
- flatMetrics
  • Displays all metric groups sequentially

- byCountryMetrics
  • Displays metrics grouped under expandable country sections
  • TODO: countriesDisplayConfig population + nested FormGroups
    per country are pending implementation in the component


Rendering Matrix
------------------------------------------------------------

  refreshedUI
    ├── flatMetrics
    │     → uses metricConfigRenderer (layout === 'flat')
    └── byCountryMetrics
          → uses metricConfigRenderer (layout === 'byCountry')
          → one accordion section per CountryMetricGroup
          → formGroupName scopes controls per country


Template Structure
------------------------------------------------------------

  #metricConfigRenderer (ng-template)
    → shared renderer for a single MetricConfig group
    → accepts context: { $implicit, index, country }
    → country is null for flatMetrics, countryCode string for byCountryMetrics

  #flatMetrics (ng-template)
    → iterates baseMetricsDisplayConfig sequentially
    → passes modifierForm as form context via outer <form>

  #byCountryMetrics (ng-template)
    → iterates countriesDisplayConfig
    → renders an accordion section per country
    → uses [formGroupName]="country.countryCode" to scope controls


+--------------------------------------+
|              FORM                    |
|  ┌── #flatMetrics                   |
|  │   (layout === 'flat')            |
|  │                                   |
|  └── #byCountryMetrics              |
|      (layout === 'byCountry')        |
+--------------------------------------+
                  ↓
        ⚠ WARNING MESSAGE
          (showWarning)
                  ↓
+--------------------------------------+
|           ACTION BUTTONS             |
|        Preview / Clear / Save        |
+--------------------------------------+

RULE:
Warning sits between form and buttons to guide user input
before actions are triggered.
-->

<div class="neptune-overlay" [class.open]="isOpen"></div>

<div class="side-drawer fixed top-0 right-0 flex flex-col w-96 p-4 z-[101] h-full overflow-y-scroll box-border bg-white" *ngIf="!isLoading; else loadingTemplate" [class.open]="isOpen">
  <div class="side-drawer-body flex flex-col gap-2">

    <!-- Header -->
    <div class="side-drawer-header flex justify-between items-center">
      <span class="side-drawer-title text-3xl">Cost Assumptions</span>
      <div class="close-icon hover-pointer" (click)="closeSideNavBar()">
        <img src="assets/close-icon-black.svg" alt="close dialog" />
      </div>
    </div>

    <form class="side-navbar-form flex flex-col gap-4 p-4 bg-neptune-muted" [formGroup]="modifierForm">

      <!-- ============================================================ -->
      <!-- METRIC RENDERER TEMPLATE                                      -->
      <!-- Shared by both flatMetrics and byCountryMetrics layouts.      -->
      <!--                                                               -->
      <!-- Context variables:                                            -->
      <!--   $implicit → MetricConfig (the metric group to render)       -->
      <!--   index     → number (used for element IDs)                   -->
      <!--   country   → string | null                                   -->
      <!--               null  = flat layout (no country scoping)        -->
      <!--               string = byCountry layout (countryCode)         -->
      <!--                                                               -->
      <!-- NOTE: formControlName binds to the nearest Angular form       -->
      <!-- context — the root [formGroup]="modifierForm" for flat, or    -->
      <!-- the [formGroupName]="country.countryCode" wrapper for         -->
      <!-- byCountry. No explicit formGroup re-assertion is needed here. -->
      <!-- ============================================================ -->
      <ng-template #metricConfigRenderer let-metricConfig let-index="index" let-formGroup="formGroup" let-country="country">

        <div [formGroup]="formGroup" class="flex flex-col gap-6">
          <div class="side-navbar-metrics-header text-2xl pb-2 neptune-border-b-2 border-sky-blue" id="side-navbar-metrics-header-{{index}}">
            {{metricConfig.metricName}}
          </div>

          <div class="side-navbar-metrics-body flex flex-col">
            <!-- Optional subheader -->
            <p *ngIf="metricConfig.metricSubHeader && !metricConfig.hideSubHeader" class="side-navbar-metrics-subheader text-base" id="side-navbar-metrics-subheader-{{index}}">
              {{metricConfig.metricSubHeader}}
            </p>

            <!-- Show message if no metrics are available -->
            <div *ngIf="!metricConfig.hasVisibleMetrics" class="no-metrics-message">
              No TVD for the filter selections, changing cost assumption not available.
            </div>

            <!-- Metrics -->
            <div class="flex flex-col gap-4">
              <div *ngFor="let metric of metricConfig.metrics; let isLast = last" [hidden]="metric.isHidden" [ngClass]="{'neptune-border-b-1 border-sky-blue' : metricConfig.metricSubHeader == '' && !isLast}">

                <label class="side-navbar-metrics-label text-sm font-bold pb-2" id="side-navbar-metrics-label-{{metric.id}}" for="{{metric.id}}">
                  {{metric.title}}
                  <span *ngIf="!metric.isTwoFactorsApplicable">{{getUOM(metric.id)}}</span>
                </label>

                <!-- Single-factor input -->
                <div *ngIf="!metric.isTwoFactorsApplicable">
                  <input
                    class="side-navbar-metrics-input neptune-input-background border-solid border-1 rounded box-border w-full"
                    id="side-navbar-metrics-input-{{metric.id}}"
                    type="text"
                    formControlName="{{metric.id}}"
                    appTwoDigitDecimal
                    [inputModifierForm]="formGroup"
                    [metricId]="metric.id"
                    autocomplete="off"
                  />
                </div>

                <!-- Two-factor inputs -->
                <div *ngIf="metric.isTwoFactorsApplicable" class="side-navbar-twofactors">
                  <div *ngFor="let twofactorMetrics of metric.twoFactorMetrics" class="twofactors-container flex flex-col pb-2"
                    [ngClass]="{'single-metric': metric.twoFactorMetrics?.length === 1}">
                    <label class="twofactors-container-label" for="{{twofactorMetrics.id}}">{{twofactorMetrics.title}} {{getUOM(twofactorMetrics.id ?? '')}}</label>
                    <input
                      class="twofactors-container-input border-solid border-1 rounded box-border w-full"
                      [ngClass]="{'neptune-input-background': twofactorMetrics.isCurrencyVisible}"
                      id="{{twofactorMetrics.id}}"
                      type="text"
                      formControlName="{{twofactorMetrics.id}}"
                      appTwoDigitDecimal
                      [inputModifierForm]="formGroup"
                      [metricId]="twofactorMetrics?.id ?? ''"
                      autocomplete="off"
                    />
                    <div *ngIf="twofactorMetrics?.isDefaultValueVisible" id="twofactors-container-{{twofactorMetrics.id}}" class="side-navbar-metrics-default">
                      <ng-container *ngIf="twofactorMetrics?.isCurrencyVisible">Default {{getCurrencyCode() | currencySymbol}}{{getDefaultRate(twofactorMetrics.id ?? '', country)}}</ng-container>
                      <ng-container *ngIf="!twofactorMetrics?.isCurrencyVisible">Default {{getDefaultRate(twofactorMetrics.id ?? '', country, twofactorMetrics?.isDecimal)}} {{twofactorMetrics?.staticUOM ?? ''}}</ng-container>
                    </div>
                    <div id="twofactors-error-{{twofactorMetrics.id}}" class="side-navbar-error-message text-xs text-red-500">
                      <span *ngIf="formGroup.get(twofactorMetrics?.id ?? '')?.errors?.['min'] && formGroup.get(twofactorMetrics?.id ?? '')?.touched">
                        {{ metric.twoFactorMetrics?.length === 1
                          ? metric.title + ' value must be greater than 0 or a valid value must be entered.'
                          : 'value must be greater than 0.' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Default value (single-factor) -->
                <div *ngIf="!metric.isTwoFactorsApplicable" id="side-navbar-metrics-default-{{metric.id}}" class="side-navbar-metrics-default text-sm pb-2">
                  Default {{getCurrencyCode() | currencySymbol}}{{getDefaultRate(metric.id, country)}}
                </div>

                <!-- Error message (single-factor) -->
                <div *ngIf="!metric.isTwoFactorsApplicable" id="side-navbar-error-message-{{metric.id}}" class="side-navbar-error-message text-xs text-red-500">
                  <span *ngIf="formGroup.get(metric.id)?.errors?.['min'] && formGroup.get(metric.id)?.touched">
                    {{metric.title}} value must be greater than 0 or a valid value must be entered.
                  </span>
                </div>

              </div>
            </div>
          </div>
        </div>

      </ng-template>
      <!-- END #metricConfigRenderer -->


      <!-- ============================================================ -->
      <!-- FLAT LAYOUT TEMPLATE                                          -->
      <!-- Renders all metric groups sequentially.                      -->
      <!-- ============================================================ -->
      <ng-template #flatMetrics>
        <div *ngFor="let metricConfig of baseMetricsDisplayConfig; let i = index">
          <ng-container
            *ngTemplateOutlet="
              metricConfigRenderer;
              context: {
                $implicit: metricConfig,
                index: i,
                formGroup: modifierForm,
                country: null
              }
            ">
          </ng-container>
        </div>
      </ng-template>


      <!-- ============================================================ -->
      <!-- BY-COUNTRY LAYOUT TEMPLATE                                    -->
      <!-- Renders metrics grouped under collapsible country sections.   -->
      <!--                                                               -->
      <!-- TODO (byCountryMetrics implementation):                       -->
      <!--   1. Populate countriesDisplayConfig in the component by      -->
      <!--      grouping baseMetricsDisplayConfig by country code.       -->
      <!--   2. Add a nested FormGroup per countryCode to modifierForm   -->
      <!--      so [formGroupName] resolves correctly.                   -->
      <!--   3. Update initializeForm() to build per-country controls.   -->
      <!-- ============================================================ -->
      <ng-template #byCountryMetrics>
        <div *ngFor="let country of countriesDisplayConfig" class="flex flex-col gap-3">

          <!-- Accordion header -->
          <div class="flex justify-between items-center py-3 neptune-border-b-1 border-sky-blue cursor-pointer"
              (click)="toggleCountry(country)">
            <span class="font-semibold text-2xl">{{ country.countryName }}</span>
            <img
              src="assets/shared-icons/neptune-icon-blue-chevron-right.svg"
              class="w-4 h-4 transition-transform"
              [ngClass]="country.expanded ? 'rotate-90' : ''"
              alt="expand/collapse"
            />
          </div>

          <!-- Accordion body (expanded only) -->
          <div *ngIf="country.expanded" [formGroupName]="country.countryCode" class="flex flex-col gap-6">
            <div *ngFor="let metricConfig of country.metricsDisplayConfig; let i = index">
              <ng-container
                *ngTemplateOutlet="
                  metricConfigRenderer;
                  context: {
                    $implicit: metricConfig,
                    index: i,
                    formGroup: modifierForm.get(country.countryCode),
                    country: country.countryCode
                  }
                ">
              </ng-container>
            </div>
          </div>

        </div>
      </ng-template>
      <!-- END #byCountryMetrics -->


      <!-- ============================================================ -->
      <!-- LAYOUT EXECUTION                                              -->
      <!-- Only one branch is active at a time based on resolved layout. -->
      <!-- ============================================================ -->
      <ng-container *ngIf="layout === 'flat'">
        <ng-container *ngTemplateOutlet="flatMetrics"></ng-container>
      </ng-container>

      <ng-container *ngIf="layout === 'byCountry'">
        <ng-container *ngTemplateOutlet="byCountryMetrics"></ng-container>
      </ng-container>

    </form>

    <!-- Preview dialog -->
    <div *ngIf="isPreview">
      <app-tvd-dialog
        [state]="previewState"
        (save)="saveCostAssumptionsMetrics()"
        (closeModal)="closeModalPopUp($event)">
      </app-tvd-dialog>
    </div>

  </div>

    <!-- Warning -->
  <div *ngIf="showWarning" class="warning-message px-4 pb-4 text-xs text-red-500">
    Please at least fill in one metric value.
  </div>

    <!-- Buttons -->
  <div class="side-navbar-button-section flex items-stretch pt-4 gap-6 border-solid neptune-border-t-1 border-border-gray" *ngIf="hasAdjustableMetrics">
    <button
      id="side-navbar-preview-button"
      class="side-navbar-button neptune-button-white flex-1 px-6 py-4"
      [ngClass]="{'bg-opacity-50 cursor-not-allowed': !hasValidMetricInput}"
      [disabled]="modifierForm.invalid"
      (click)="previewComponentData()">
        Preview
    </button>
    <button
      id="side-navbar-clear-button"
      class="side-navbar-button neptune-button-white flex-1 px-6 py-4"
      [ngClass]="{'bg-opacity-50 cursor-not-allowed': !hasValidMetricInput}"
      [disabled]="modifierForm.invalid"
      (click)="resetForm()">
      Clear
    </button>
    <button
      id="side-navbar-save-button"
      class="side-navbar-button neptune-button-blue flex-1 px-6 py-4"
      [ngClass]="{'bg-opacity-50 cursor-not-allowed': !hasValidMetricInput}"
      [disabled]="modifierForm.invalid"
      (click)="saveCostAssumptionsMetrics()">
      Save
    </button>
  </div>

</div>

<ng-template #loadingTemplate>
  <div class="neptune-modal-cover">
    <loader [size]="'medium'" *ngIf="isLoading"></loader>
  </div>
</ng-template>
